/**
 * Formats a number as a currency string.
 *
 * @param n - The number to format.
 * @param options - Formatting options including currency, minimumFractionDigits, and maximumFractionDigits.
 * @returns The formatted currency string.
 */
export const formatPriceNumber = (
  n: number,
  { currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 2 }: Intl.NumberFormatOptions = {},
) => {
  if (n == null) return '';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits,
    minimumFractionDigits,
  });
};

/**
 * Formats a number with a 'K' suffix if it is in the thousands.
 *
 * @param n - The number to format.
 * @returns The formatted string with 'K' suffix if applicable.
 */
export const formatNumberAsThousandCurrency = (number: number, currency: string = 'USD') => {
  let formattedNumber;
  let suffix = '';

  if (number >= 1000) {
    formattedNumber = (number / 1000).toFixed(2);
    suffix = 'K';
  } else {
    formattedNumber = number.toFixed(2);
  }

  // Extract the parts of the currency formatted string
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: Number.isInteger(Number(formattedNumber)) ? 0 : 2,
    maximumFractionDigits: 2,
  }).formatToParts(Number(formattedNumber));

  // Reconstruct the formatted string with the 'K' suffix in the correct place
  let result = '';
  let addedSuffix = false;

  parts.forEach((part) => {
    if (part.type === 'currency') {
      result += part.value;
    } else if (part.type === 'group') {
      // Skip the group separator (e.g., ',')
    } else if (part.type === 'fraction') {
      result += part.value + suffix;
      addedSuffix = true;
    } else {
      result += part.value;
    }
  });

  // Handle edge cases where the suffix needs to be added explicitly
  if (!addedSuffix) {
    result += suffix;
  }

  return result;
};

/**
 * Formats a number with a specified number of fraction digits.
 *
 * @param n - The number to format.
 * @param precision - The number of fraction digits to include.
 * @returns The formatted number string.
 */
export const formatNumber = (n: number, precision = 0) => {
  const _number = isNaN(n) ? 0 : n;
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: precision, maximumFractionDigits: 2 }).format(_number);
};

/**
 * Formats a number with a 'K' suffix if it is in the thousands.
 *
 * @param n - The number to format.
 * @returns The formatted string with 'K' suffix if applicable.
 */
export const thousandFormat = (n: number) => {
  return n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n.toFixed(0);
};

export const formatPercent = (value: number, precision = 0) => {
  return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: precision, maximumFractionDigits: 2 }).format(value);
};
